// components/Statistics.js - With total point labels above bars
import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import './Statistics.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';


const Statistics = ({ onBack, user, adminTitle, forUserId }) => {
  const [chartData, setChartData] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Aggregation controls
  const [aggregationType, setAggregationType] = useState('day'); // day, week, month
  const [numPeriods, setNumPeriods] = useState(7);
  const [tempPeriods, setTempPeriods] = useState(7);

  // Field order - sorted by point value (highest at bottom of stack = most prominent)
  const fieldOrder = [
    'email',              // 1 pt - top of stack
    'call',               // 1 pt
    'opp_created',        // 2 pts
    'knock',              // 2 pts
    'networking_event',   // 5 pts
    'ac_partner',         // 5 pts
    'appt_attended',      // 5 pts
    'appt_set',           // 5 pts
    'rsc_observation',    // 5 pts
    'rsa_observation',    // 5 pts
    'proposals_delivered', // 10 pts
    'motis_appointment',  // 10 pts
    'team_huddle',        // 10 pts
    'sale',               // 20 pts - bottom of stack (most visible)
  ];

  const fieldLabels = {
    knock: 'Knock',
    call: 'Call',
    opp_created: 'Opp Created',
    appt_set: 'Appt Set',
    appt_attended: 'Appt Attended',
    proposals_delivered: 'Proposals',
    sale: 'Sale',
    ac_partner: 'AC Partner',
    networking_event: 'Networking',
    motis_appointment: 'MOTIS Appt',
    email: 'Email',
    rsc_observation: 'RSC Obs.',
    rsa_observation: 'RSA Obs.',
    team_huddle: 'Team Huddle',
  };

  // Original color palette
  const fieldColors = {
    knock: '#e57373',
    call: '#81c784',
    opp_created: '#64b5f6',
    appt_set: '#ffb74d',
    appt_attended: '#9575cd',
    proposals_delivered: '#26a69a',
    sale: '#4db6ac',
    ac_partner: '#f06292',
    networking_event: '#dce775',
    motis_appointment: '#ff8a65',
    email: '#ba68c8',
    rsc_observation: '#80deea',
    rsa_observation: '#ce93d8',
    team_huddle: '#a5d6a7',
  };

  // Point values for each field
  const pointValues = {
    knock: 2,
    call: 1,
    opp_created: 2,
    appt_set: 5,
    appt_attended: 5,
    proposals_delivered: 10,
    sale: 20,
    ac_partner: 5,
    networking_event: 5,
    motis_appointment: 10,
    rsc_observation: 5,
    rsa_observation: 5,
    team_huddle: 10,
    email: 1,
  };

  useEffect(() => {
    fetchAllLogs();
  }, []);

  useEffect(() => {
    if (allLogs.length > 0) {
      processData();
    }
  }, [allLogs, aggregationType, numPeriods]);

  const fetchAllLogs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/logs/${forUserId ? `?for_user_id=${forUserId}` : ''}`, {
        headers: {
          'X-User-ID': user.userId,  // Now user is defined
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data = await response.json();
      setAllLogs(data);
    } catch (err) {
      setError('Error fetching statistics. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const processData = () => {
    switch (aggregationType) {
      case 'day':
        setChartData(processDailyData());
        break;
      case 'week':
        setChartData(processWeeklyData());
        break;
      case 'month':
        setChartData(processMonthlyData());
        break;
      default:
        setChartData([]);
    }
  };

  const processDailyData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = numPeriods - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const dayData = {
        label: formatDate(date),
        ...getEmptyFieldsObject(),
        period: toLocalISODate(date),
        total: 0
      };
      
      // Find log for this day
      const log = allLogs.find(log => log.date === dayData.period);
      if (log) {
        fieldOrder.forEach(field => {
          // Store both count and points
          const points = (log[field] || 0) * pointValues[field];
          dayData[field] = points;
          dayData[`${field}_count`] = log[field] || 0;
          dayData.total += points;
        });
      }
      
      data.push(dayData);
    }
    
    return data;
  };

  const processWeeklyData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = numPeriods - 1; i >= 0; i--) {
      // Get the start of the week (Monday)
      const endDate = new Date(today);
      endDate.setDate(today.getDate() - (i * 7));
      
      const startDate = new Date(endDate);
      const currentDay = endDate.getDay();
      const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1;
      startDate.setDate(endDate.getDate() - daysToSubtract);
      
      const weekData = {
        label: `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`,
        ...getEmptyFieldsObject(),
        period: toLocalISODate(startDate),
        total: 0
      };

      // Aggregate logs for this week
      const weekLogs = allLogs.filter(log => {
        const logDate = parseLocalDate(log.date);
        return logDate >= startDate && logDate <= endDate;
      });
      
      weekLogs.forEach(log => {
        fieldOrder.forEach(field => {
          const points = (log[field] || 0) * pointValues[field];
          weekData[field] += points;
          weekData[`${field}_count`] = (weekData[`${field}_count`] || 0) + (log[field] || 0);
          weekData.total += points;
        });
      });
      
      data.push(weekData);
    }
    
    return data;
  };

  const processMonthlyData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = numPeriods - 1; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthData = {
        label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        ...getEmptyFieldsObject(),
        period: toLocalISODate(monthStart),
        total: 0
      };

      // Aggregate logs for this month
      const monthLogs = allLogs.filter(log => {
        const logDate = parseLocalDate(log.date);
        return logDate >= monthStart && logDate <= monthEnd;
      });
      
      monthLogs.forEach(log => {
        fieldOrder.forEach(field => {
          const points = (log[field] || 0) * pointValues[field];
          monthData[field] += points;
          monthData[`${field}_count`] = (monthData[`${field}_count`] || 0) + (log[field] || 0);
          monthData.total += points;
        });
      });
      
      data.push(monthData);
    }
    
    return data;
  };

  const getEmptyFieldsObject = () => {
    const fields = {};
    fieldOrder.forEach(field => {
      fields[field] = 0;
      fields[`${field}_count`] = 0;
    });
    return fields;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateShort = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format date as YYYY-MM-DD in LOCAL timezone (not UTC)
  const toLocalISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse a YYYY-MM-DD string as LOCAL date (not UTC)
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleAggregationChange = (e) => {
    const newType = e.target.value;
    setAggregationType(newType);
    
    // Set default periods based on aggregation type
    switch (newType) {
      case 'day':
        setNumPeriods(7);
        setTempPeriods(7);
        break;
      case 'week':
        setNumPeriods(4);
        setTempPeriods(4);
        break;
      case 'month':
        setNumPeriods(6);
        setTempPeriods(6);
        break;
    }
  };

  const handlePeriodsSubmit = (e) => {
    e.preventDefault();
    setNumPeriods(Math.max(1, parseInt(tempPeriods) || 1));
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const firstPayload = payload[0];
      const totalPoints = firstPayload && firstPayload.payload ? firstPayload.payload.total : 0;
      
      return (
        <div className="custom-tooltip">
          <p className="label">{label}</p>
          {payload.map((entry, index) => {
            const field = entry.dataKey;
            const count = entry.payload && entry.payload[`${field}_count`] ? entry.payload[`${field}_count`] : 0;
            const points = entry.value || 0;
            const pointValue = pointValues[field] || 0;
            
            return points > 0 && (
              <div key={index}>
                <p style={{ color: entry.color }}>
                  {`${fieldLabels[field]}: ${points} points`}
                </p>
                <p style={{ fontSize: '0.85em', color: '#8a90a0' }}>
                  {`(${count} × ${pointValue} = ${points})`}
                </p>
              </div>
            );
          })}
          {totalPoints > 0 && (
            <div style={{ marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #3d4455' }}>
              <p style={{ fontWeight: 'bold', color: '#e8eaed' }}>Total: {totalPoints} points</p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom label component for displaying total points above bars
   // Custom label component for displaying total points above bars
  const TotalLabel = (props) => {
    const { x, y, width, payload } = props;
    
    // Safety check: ensure payload exists and has total property
    if (!payload || typeof payload.total === 'undefined') return null;
    
    const total = payload.total;
    
    if (total === 0) return null;
    
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#e8eaed"
        textAnchor="middle"
        fontSize="12"
        fontWeight="bold"
      >
        {total}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="statistics">
        <div className="header">
          <button onClick={onBack} className="back-button">← Back to Menu</button>
          <h2>Statistics</h2>
        </div>
        <div className="loading">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics">
        <div className="header">
          <button onClick={onBack} className="back-button">← Back to Menu</button>
          <h2>Statistics</h2>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="statistics">
      <div className="header">
        <button onClick={onBack} className="back-button">← Back to {adminTitle ? 'Users' : 'Menu'}</button>
        <h2>{adminTitle || 'Statistics'}</h2>
      </div>
      
      <div className="chart-container">
        <div className="controls">
          <div className="aggregation-controls">
            <label htmlFor="aggregation-type">Aggregation:</label>
            <select 
              id="aggregation-type"
              value={aggregationType} 
              onChange={handleAggregationChange}
              className="aggregation-select"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          
          <form onSubmit={handlePeriodsSubmit} className="periods-form">
            <label htmlFor="num-periods">
              Show last {aggregationType === 'day' ? 'days' : aggregationType === 'week' ? 'weeks' : 'months'}:
            </label>
            <input
              id="num-periods"
              type="number"
              min="1"
              max="100"
              value={tempPeriods}
              onChange={(e) => setTempPeriods(e.target.value)}
              className="periods-input"
            />
            <button type="submit" className="apply-button">Apply</button>
          </form>
        </div>

        <div className="chart">
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={chartData}
              margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#3d4455" />
              <XAxis
                dataKey="label"
                angle={aggregationType === 'day' ? -45 : 0}
                textAnchor={aggregationType === 'day' ? 'end' : 'middle'}
                height={aggregationType === 'day' ? 80 : 50}
                tick={{ fill: '#b0b5c0', fontSize: 14 }}
                axisLine={{ stroke: '#454c5e' }}
              />
              <YAxis
                label={{ value: 'Points', angle: -90, position: 'insideLeft', fill: '#b0b5c0', fontSize: 14 }}
                tick={{ fill: '#b0b5c0', fontSize: 14 }}
                axisLine={{ stroke: '#454c5e' }}
              />
              <Tooltip content={customTooltip} cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }} />
              <Legend />
              {fieldOrder.map(field => (
                <Bar
                  key={field}
                  dataKey={field}
                  stackId="a"
                  fill={fieldColors[field]}
                  name={fieldLabels[field]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
